import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, ShieldAlert, Copy, KeyRound } from 'lucide-react';

// TOTP two-factor enrolment + management (authenticator apps + one-time backup codes).
export const MfaSettings = () => {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);   // { secret, otpauth_uri, qr }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState('');

  const load = useCallback(async () => {
    try { const { data } = await api.get('/auth/mfa/status'); setStatus(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post('/auth/mfa/setup'); setSetup(data); setBackupCodes(null); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo iniciar la configuración'); } finally { setBusy(false); }
  };

  const confirmSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post('/auth/mfa/verify-setup', { code: code.trim() }); setBackupCodes(data.backup_codes); setSetup(null); setCode(''); toast.success('Verificación en dos pasos activada'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Código incorrecto'); } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try { await api.post('/auth/mfa/disable', { code: disableCode.trim() }); setDisableCode(''); toast.success('Verificación en dos pasos desactivada'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Código incorrecto'); } finally { setBusy(false); }
  };

  const copyBackup = () => { navigator.clipboard?.writeText((backupCodes || []).join('\n')); toast.success('Códigos de respaldo copiados'); };

  return (
    <div className="bg-card rounded-xl border border-border p-6 mt-6" data-testid="mfa-settings-card">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-bold text-lg flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Verificación en dos pasos (2FA)</h2>
        {status && <Badge variant="outline" data-testid="mfa-status-badge">{status.enabled ? 'Activada' : 'Desactivada'}</Badge>}
      </div>
      <p className="text-sm text-muted-foreground mt-1">Añade una capa extra de seguridad con una app de autenticación (Google/Microsoft Authenticator).</p>

      {status?.required && !status?.enabled && (
        <Alert className="mt-3 border-brand-orange/40" data-testid="mfa-required-alert"><ShieldAlert className="h-4 w-4" /><AlertDescription>Tu rol (SuperAdmin o personal) <strong>requiere</strong> verificación en dos pasos. Actívala ahora para proteger la cuenta.</AlertDescription></Alert>
      )}

      {backupCodes && (
        <Alert className="mt-4" data-testid="mfa-backup-codes">
          <KeyRound className="h-4 w-4" />
          <AlertDescription>
            <p className="font-semibold mb-2">Guarda tus códigos de respaldo (se muestran una sola vez):</p>
            <div className="grid grid-cols-2 gap-1 font-mono text-sm bg-muted/60 p-3 rounded-md">{backupCodes.map((c) => <span key={c}>{c}</span>)}</div>
            <Button size="sm" variant="outline" className="mt-3" onClick={copyBackup} data-testid="mfa-copy-backup"><Copy className="h-4 w-4" /> Copiar códigos</Button>
          </AlertDescription>
        </Alert>
      )}

      {!status?.enabled && !setup && (
        <Button className="mt-4 bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 rounded-full" disabled={busy} onClick={startSetup} data-testid="mfa-enable-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Activar 2FA</Button>
      )}

      {setup && (
        <div className="mt-4 space-y-3" data-testid="mfa-setup-panel">
          <p className="text-sm">1. Escanea este QR con tu app de autenticación:</p>
          <img src={setup.qr} alt="Código QR de configuración 2FA" className="h-44 w-44 border border-border rounded-lg bg-white p-2" data-testid="mfa-qr" />
          <p className="text-xs text-muted-foreground break-all">o introduce la clave manualmente: <code className="font-mono">{setup.secret}</code></p>
          <div className="max-w-xs"><Label className="text-xs">2. Introduce el código de 6 dígitos</Label><Input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="mfa-verify-code" className="mt-1 tracking-widest" placeholder="123456" /></div>
          <div className="flex gap-2"><Button disabled={busy || code.trim().length < 6} onClick={confirmSetup} data-testid="mfa-verify-btn" className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verificar y activar'}</Button><Button variant="ghost" onClick={() => { setSetup(null); setCode(''); }} data-testid="mfa-cancel-btn">Cancelar</Button></div>
        </div>
      )}

      {status?.enabled && (
        <div className="mt-4 space-y-2" data-testid="mfa-disable-panel">
          <p className="text-sm text-muted-foreground">Códigos de respaldo restantes: <strong>{status.backup_codes_remaining}</strong></p>
          <div className="max-w-xs"><Label className="text-xs">Introduce un código para desactivar</Label><Input inputMode="numeric" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} data-testid="mfa-disable-code" className="mt-1 tracking-widest" placeholder="123456" /></div>
          <Button variant="destructive" disabled={busy || disableCode.trim().length < 4} onClick={disable} data-testid="mfa-disable-btn" className="rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Desactivar 2FA'}</Button>
        </div>
      )}
    </div>
  );
};
